import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getDisplayPlanName } from "@/lib/planNames";
import "@/styles/account.css";

export default function BillingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/stripe/portal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || "Failed to open billing portal");
      }
    } catch {
      alert("Failed to open billing portal. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const currentPlan = user?.plan || "starter";
  const displayName = getDisplayPlanName(currentPlan, user?.subscription_status, user?.email);
  const isPaid = ["pro", "agent"].includes(currentPlan) && user?.subscription_status && !["canceled"].includes(user.subscription_status);
  const isStarter = currentPlan === "starter";

  return (
    <div className="card simple-page-card">
      <h2>Billing</h2>

      <div className="info-box">
        <div className="info-box-label">Current Plan</div>
        <div className="info-box-value">{displayName}{isStarter && <span style={{ fontSize: 13, opacity: 0.7, marginLeft: 8 }}>Free</span>}</div>
        {isStarter && (
          <div className="info-box-desc">
            10 ChatGPT chats per month &middot; 1 Google Ads account
          </div>
        )}
        {user?.subscription_status && !isStarter && (
          <div className="info-box-desc">
            Status: <span className={`billing-status billing-status-${user.subscription_status}`}>
              {user.subscription_status}
            </span>
          </div>
        )}
        {user?.current_period_end && (
          <div className="info-box-desc">
            {user.cancel_at_period_end
              ? `Access until ${formatDate(user.current_period_end)}`
              : user.subscription_status === "trialing"
              ? `Trial ends ${formatDate(user.current_period_end)}`
              : `Renews on ${formatDate(user.current_period_end)}`
            }
          </div>
        )}
        {user?.cancel_at_period_end && (
          <div className="info-box-desc billing-cancel-notice">
            Your subscription is set to cancel at the end of the billing period.
          </div>
        )}
      </div>

      {isStarter ? (
        <div className="billing-actions">
          <p className="billing-upgrade-text">
            Upgrade to Pro or Agency for full ChatGPT access, more accounts, and advanced features.
          </p>
          <Link to="/pricing" className="btn btn-primary">
            Upgrade Now
          </Link>
        </div>
      ) : isPaid && user?.has_stripe ? (
        <div className="billing-actions">
          <button
            className="btn btn-secondary"
            onClick={handleManageBilling}
            disabled={loading}
          >
            {loading ? "Opening..." : "Manage Billing"}
          </button>
          <p className="billing-manage-desc">
            Update payment method, change plan, view invoices, or cancel.
          </p>
        </div>
      ) : isPaid ? (
        <div className="billing-actions">
          <div className="info-box-desc">
            Your plan was activated internally. No billing management needed.
          </div>
        </div>
      ) : (
        <div className="billing-actions">
          <p className="billing-upgrade-text">
            Subscribe to unlock full MCP access, API keys, and more.
          </p>
          <Link to="/pricing" className="btn btn-primary">
            View Plans
          </Link>
        </div>
      )}
    </div>
  );
}
